import api, { API_ENDPOINTS } from './api';
import axios from 'axios';
import { config } from '../config/environment';

// Spec 020: registration no longer requires a role; birthYear replaces dateOfBirth/userType
interface UserRegistrationRequest {
  firstName: string;
  lastName: string;
  email: string;
  password?: string;
  birthYear: number;
}

// Import types from centralized location
import type { ApiResponse, ChildRegistrationRequest } from './childManagementService';

export interface LoginRequest {
  email: string;
  password: string;
  userType: string;
}

export interface LoginResponse {
  token: string;
  user: {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    userType: string;
  };
  requiresActivation?: boolean;
  roleToActivate?: string;
  requiresRoleSelection?: boolean;
  activatedRoles?: Array<{ roleType: string }>;
}

export interface ActivateRoleRequest {
  phoneNumber?: string;
  schoolCountry?: string;
  schoolCouncil?: string;
  schoolName?: string;
  schoolType?: string;
  courseInterests?: string;
}

export interface AuthService {
  login: (credentials: LoginRequest) => Promise<LoginResponse>;
  loginWithGoogle: (idToken: string) => Promise<LoginResponse>;
  requestLoginCode: (email: string, userType: string) => Promise<void>;
  verifyLoginCode: (email: string, code: string, userType: string) => Promise<LoginResponse>;
  register: (userData: UserRegistrationRequest) => Promise<ApiResponse<any>>;
  registerChild: (childData: ChildRegistrationRequest) => Promise<ApiResponse<any>>;
  activateRole: (roleType: string, data: ActivateRoleRequest) => Promise<LoginResponse>;
  switchRole: (targetRole: string) => Promise<LoginResponse>;
  verifyEmail: (token: string) => Promise<void>;
  resendVerificationEmail: (email: string, recaptchaToken: string) => Promise<void>;
  logout: () => void;
  getToken: () => string | null;
  isAuthenticated: () => boolean;
}

class AuthServiceImpl implements AuthService {
  
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      // Use direct axios call to avoid interceptor issues during login
      const API_BASE_URL = config.coreApiUrl;
      const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.AUTH.LOGIN}`, credentials, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.data.success) {
        const loginResponse = response.data.data; // Backend now returns { token, user }
        
        // Store token and user data
        localStorage.setItem('authToken', loginResponse.token);
        localStorage.setItem('user', JSON.stringify(loginResponse.user));
        
        return loginResponse;
      } else {
        throw new Error(response.data.message || 'Login failed');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      // Re-throw so caller can check response.status (e.g. 403 for email not verified)
      if (error.response) {
        const err = new Error(error.response?.data?.message || 'Login failed. Please check your credentials.') as Error & { response?: { status: number; data?: { message?: string } } };
        err.response = error.response;
        throw err;
      }
      throw new Error(error.message || 'Login failed. Please check your credentials.');
    }
  }

  async loginWithGoogle(idToken: string): Promise<LoginResponse> {
    try {
      const API_BASE_URL = config.coreApiUrl;
      const response = await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.AUTH.LOGIN_GOOGLE}`,
        { idToken },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response.data.success) {
        const loginResponse = response.data.data;
        localStorage.setItem('authToken', loginResponse.token);
        localStorage.setItem('user', JSON.stringify(loginResponse.user));
        return loginResponse;
      }
      throw new Error(response.data.message || 'Google login failed');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      const message = err.response?.data?.message || err.message || 'Google login failed.';
      throw new Error(message);
    }
  }

  async requestLoginCode(email: string, userType: string): Promise<void> {
    const API_BASE_URL = config.coreApiUrl;
    try {
      const response = await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.AUTH.LOGIN_REQUEST_CODE}`,
        { email, userType },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to send login code.');
      }
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { message?: string } }; message?: string };
      if (err.response?.status === 403) {
        const e = new Error(err.response?.data?.message || 'Email not verified.') as Error & { response?: { status: number } };
        e.response = { status: 403 };
        throw e;
      }
      throw new Error(err.response?.data?.message || err.message || 'Failed to send login code.');
    }
  }

  async verifyLoginCode(email: string, code: string, userType: string): Promise<LoginResponse> {
    const API_BASE_URL = config.coreApiUrl;
    try {
      const response = await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.AUTH.LOGIN_VERIFY_CODE}`,
        { email, code, userType },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response.data.success) {
        const loginResponse = response.data.data;
        localStorage.setItem('authToken', loginResponse.token);
        localStorage.setItem('user', JSON.stringify(loginResponse.user));
        return loginResponse;
      }
      throw new Error(response.data.message || 'Code verification failed');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      throw new Error(err.response?.data?.message || err.message || 'Code verification failed.');
    }
  }

  async register(userData: UserRegistrationRequest): Promise<ApiResponse<any>> {
    try {
      // Use direct axios call to avoid interceptor issues during registration
      const API_BASE_URL = config.coreApiUrl;
      const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.AUTH.REGISTER}`, userData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      // Do not auto-login: user must verify email first, then sign in on the login page.
      return response.data;
    } catch (error: any) {
      console.error('Registration error:', error);
      throw new Error(error.response?.data?.message || 'Registration failed. Please try again.');
    }
  }

  async registerChild(childData: ChildRegistrationRequest): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(API_ENDPOINTS.AUTH.REGISTER_CHILD, childData);
      return response.data;
    } catch (error: any) {
      console.error('Child registration error:', error);
      throw new Error(error.response?.data?.message || 'Child registration failed. Please try again.');
    }
  }

  async activateRole(roleType: string, data: ActivateRoleRequest): Promise<LoginResponse> {
    try {
      const response = await api.post(`/api/users/roles/${roleType}/activate`, data);
      if (response.data?.success) {
        const result = response.data.data as LoginResponse;
        if (result.token) {
          localStorage.setItem('authToken', result.token);
          if (result.user) localStorage.setItem('user', JSON.stringify(result.user));
        }
        return result;
      }
      throw new Error(response.data?.message || 'Role activation failed');
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || 'Role activation failed');
    }
  }

  async switchRole(targetRole: string): Promise<LoginResponse> {
    try {
      const response = await api.post('/api/users/switch-role', { targetRole });
      if (response.data?.success) {
        const result = response.data.data as LoginResponse;
        if (result.token) {
          localStorage.setItem('authToken', result.token);
          if (result.user) localStorage.setItem('user', JSON.stringify(result.user));
        }
        return result;
      }
      throw new Error(response.data?.message || 'Role switch failed');
    } catch (error: any) {
      const err = error as { response?: { status?: number; data?: { message?: string } }; message?: string };
      if (err.response?.status === 409) {
        const conflict = new Error(err.response?.data?.message || 'Role not activated') as Error & { status: number };
        conflict.status = 409;
        throw conflict;
      }
      throw new Error(err.response?.data?.message || err.message || 'Role switch failed');
    }
  }

  async verifyEmail(token: string): Promise<void> {
    const API_BASE_URL = config.coreApiUrl;
    try {
      const response = await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.AUTH.VERIFY_EMAIL}`,
        null,
        { params: { token } }
      );
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Verification failed.');
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      const message = err.response?.data?.message || err.message || 'Verification failed.';
      throw new Error(message);
    }
  }

  async resendVerificationEmail(email: string, recaptchaToken: string): Promise<void> {
    const API_BASE_URL = config.coreApiUrl;
    try {
      const response = await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.AUTH.RESEND_VERIFICATION}`,
        { email, recaptchaToken },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to resend verification email.');
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      const message = err.response?.data?.message || err.message || 'Failed to resend verification email.';
      throw new Error(message);
    }
  }

  logout(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    // Redirect to home page
    window.location.href = '/';
  }

  getToken(): string | null {
    return localStorage.getItem('authToken');
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    return token !== null && token !== undefined;
  }

  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }
}

export const authService = new AuthServiceImpl();
export default authService;
